import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import { styled } from '@mui/material/styles';

const CARD_BORDER = 'rgba(34, 28, 90, 0.07)';
const TEXT_SECONDARY = '#5A5578';
const TEXT_TERTIARY = '#8782A0';
const TEXT_PRIMARY = '#141030';
const FIELD_BG = 'rgba(248, 246, 253, 0.6)';
const FIELD_BORDER = 'rgba(34, 28, 90, 0.12)';

export const PageRoot = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

/** Lifts the hero text above the decorative blur circles behind it */
export const HeroTitleBlock = styled(Box)({
  position: 'relative',
});

export const HeroTitle = styled('h1')({
  margin: 0,
  fontSize: 24,
  fontWeight: 600,
  lineHeight: '32px',
  letterSpacing: '-0.025em',
});

export const ContentArea = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const LoadingBox = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  padding: '48px 0',
});

export const SettingsCard = styled(Paper)({
  padding: 24,
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: 14,
  boxShadow: '0 1px 2px rgba(34,28,90,0.04), 0 8px 24px -8px rgba(34,28,90,0.06)',
});

export const CardTitle = styled('h2')({
  margin: 0,
  fontSize: '1rem',
  fontWeight: 600,
  color: TEXT_PRIMARY,
});

/** CardTitle variant for cards with no subtext/badge directly below it */
export const CardTitleSpaced = styled(CardTitle)({
  marginBottom: 20,
});

/** CardTitle variant for cards with a subtext line directly below it */
export const CardTitleTight = styled(CardTitle)({
  marginBottom: 4,
});

/** Card header holding a title on the left and a badge on the right */
export const CardHeaderRow = styled(Box)({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: 24,
});

export const TwoColumnRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(3),
  flexWrap: 'wrap',
  '& > *': { flex: '1 1 200px', minWidth: 0 },
}));

/** Vertical stack of field rows within a card */
export const FieldStack = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: 20,
});

export const FieldLabel = styled('p')({
  margin: 0,
  fontSize: '0.75rem',
  color: TEXT_TERTIARY,
});

/** FieldLabel variant used as a card's descriptive subtext, with space below it */
export const CardSubtext = styled(FieldLabel)({
  marginBottom: 20,
});

export const FieldValue = styled('p')({
  margin: '4px 0 0',
  fontSize: '0.875rem',
  color: '#141030',
});

export const FieldHelperText = styled('p')({
  margin: '4px 0 0',
  fontSize: '0.6875rem',
  color: TEXT_TERTIARY,
});

export const FieldGroupLabel = styled('label')({
  display: 'block',
  marginBottom: 6,
  fontSize: '0.75rem',
  fontWeight: 500,
  color: TEXT_SECONDARY,
});

export const ReadOnlyBadge = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  borderRadius: 9999,
  padding: '4px 10px',
  fontSize: '0.6875rem',
  fontWeight: 500,
  color: TEXT_TERTIARY,
  backgroundColor: 'rgba(248, 246, 253, 1)',
});

export const DisabledFieldGroup = styled(Box)({
  pointerEvents: 'none',
  userSelect: 'none',
  opacity: 0.6,
});

/** Tinted-background field treatment for MUI TextField/Autocomplete, whose root wraps a nested .MuiOutlinedInput-root */
export const settingsFieldSx = {
  '& .MuiOutlinedInput-root': {
    height: 40,
    borderRadius: '10px',
    backgroundColor: FIELD_BG,
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: `${FIELD_BORDER} !important` },
} as const;

/** Same tinted-background treatment for MUI Select, whose root IS the .MuiOutlinedInput-root itself */
export const settingsSelectSx = {
  height: 40,
  borderRadius: '10px',
  backgroundColor: FIELD_BG,
  fontSize: '0.875rem',
  fontWeight: 500,
  '& .MuiOutlinedInput-notchedOutline': { borderColor: FIELD_BORDER },
  '&.Mui-disabled .MuiOutlinedInput-notchedOutline': { borderColor: `${FIELD_BORDER} !important` },
  '& .MuiSelect-icon': { fontSize: 16, opacity: 0.6 },
} as const;

/** Width and monospace treatment for the HEX code TextField */
export const hexFieldSx = {
  width: 144,
  '& .MuiOutlinedInput-root': { borderRadius: '10px', fontFamily: 'monospace' },
} as const;

/** Row pairing the color picker swatch with the HEX input beside it */
export const ColorPickerRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 20,
});

export const HexFieldColumn = styled(Box)({
  flex: 1,
});

export const HexInputRow = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
});

export const SwatchGrid = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 8,
});

export const ColorSwatch = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'swatchColor' && prop !== 'isSelected',
})<{ swatchColor: string; isSelected?: boolean }>(({ swatchColor, isSelected }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 36,
  height: 36,
  borderRadius: '50%',
  backgroundColor: swatchColor,
  cursor: 'pointer',
  boxSizing: 'border-box',
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  ...(isSelected
    ? { boxShadow: `0 0 0 2px #fff, 0 0 0 4px ${swatchColor}` }
    : { '&:hover': { transform: 'scale(1.1)' } }),
}));

export const ColorPickerWrapper = styled(Box)<{ dotcolor: string }>(({ dotcolor }) => ({
  position: 'relative',
  width: 48,
  height: 48,
  flexShrink: 0,
  borderRadius: 20,
  '&:focus-within': {
    outline: `3px solid ${dotcolor}`,
    outlineOffset: 3,
  },
}));

export const ColorCircle = styled(Box)<{ dotcolor: string }>(({ dotcolor }) => ({
  width: 48,
  height: 48,
  borderRadius: 20,
  backgroundColor: dotcolor,
  flexShrink: 0,
  cursor: 'pointer',
  boxShadow: '0 1px 2px rgba(34,28,90,0.06)',
  transition: 'transform 0.18s ease, box-shadow 0.18s ease, background-color 0.5s ease-out',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: `0 0 0 3px ${dotcolor}33, 0 4px 12px ${dotcolor}33`,
  },
}));

export const ResetButton = styled(Button)({
  height: 32,
  padding: '5px 12px',
  borderRadius: 10,
  fontSize: '0.75rem',
  fontWeight: 600,
  gap: 6,
  textTransform: 'none',
  color: TEXT_PRIMARY,
  backgroundColor: '#fff',
  border: '1px solid rgba(201, 182, 255, 0.4)',
  boxShadow: '0 1px 2px rgba(34,28,90,0.04)',
  '&:hover': {
    backgroundColor: 'rgba(248, 246, 253, 0.6)',
    border: '1px solid rgba(201, 182, 255, 0.4)',
    boxShadow: '0 1px 2px rgba(34,28,90,0.04)',
  },
  '& .MuiButton-startIcon': {
    marginRight: 0,
  },
});

export const ColorPickerBox = styled('input')({
  position: 'absolute',
  inset: 0,
  opacity: 0,
  cursor: 'pointer',
  width: '100%',
  height: '100%',
  border: 'none',
  padding: 0,
  borderRadius: 20,
});
